import type { DotProps } from "@/v2-backoffice-client/components/dot"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"

// The GraphQL IdentitySyncStatus enum, as it reaches the client (string
// values). Kept as string literals so call sites don't have to import the
// generated enum object at runtime.
export type IdentitySyncStatusValue = UserFragment["IdentitySyncStatus"]

export const IDENTITY_SYNC_STATUS_IN_SYNC = "IN_SYNC"
export const IDENTITY_SYNC_STATUS_EMAIL_MISMATCH = "EMAIL_MISMATCH"
export const IDENTITY_SYNC_STATUS_NO_EMAIL_LOGIN = "NO_EMAIL_LOGIN"
export const IDENTITY_SYNC_STATUS_MISSING_IN_AZURE = "MISSING_IN_AZURE"
// Typed as the enum's value-literal union so enum-typed fields compare
// against it with no casts (string-enum members are assignable to their
// literal types); the file's zero-runtime-import contract holds.
export const IDENTITY_SYNC_STATUS_FUSION_LOGIN: `${IdentitySyncStatusValue}` =
  "FUSION_LOGIN"

export type IdentityStatusConfig = {
  label: string
  variant: DotProps["variant"]
  description: string
  // The repair action an operator should reach for.
  fix: string
}

// Only the actionable (out-of-sync) states carry a config; IN_SYNC / UNKNOWN
// are handled separately so the table stays clean.
const OUT_OF_SYNC_CONFIG: Record<string, IdentityStatusConfig> = {
  [IDENTITY_SYNC_STATUS_EMAIL_MISMATCH]: {
    label: "Email out of sync",
    variant: "warning",
    description:
      "The email this user signs in with in Azure AD B2C differs from the email on record here.",
    fix: "Fix with “Change email”.",
  },
  [IDENTITY_SYNC_STATUS_NO_EMAIL_LOGIN]: {
    label: "Legacy login",
    variant: "orange",
    description:
      "A federated/member account with no email sign-in, so its login email can’t be changed in place.",
    fix: "Fix with “Recreate login”.",
  },
  [IDENTITY_SYNC_STATUS_MISSING_IN_AZURE]: {
    label: "Missing in Azure",
    variant: "destructive",
    description:
      "This user has no matching object in Azure AD B2C, so they can’t sign in.",
    fix: "Fix with “Recreate login”.",
  },
  // Not out-of-sync — the migrated end state (FUS-927). Carried in this
  // config so the badge stays VISIBLE (a migrated user reading as blank
  // would look unchecked), with an explanation instead of a repair;
  // getIdentityFix below deliberately returns no action for it, and the
  // backend refuses “Recreate login” for these users regardless.
  [IDENTITY_SYNC_STATUS_FUSION_LOGIN]: {
    label: "Fusion login",
    variant: "success-1",
    description:
      "This user signs in with Fusion login (Entra External ID). The Azure AD B2C check no longer applies to them.",
    fix: "No action needed.",
  },
}

export function getIdentityStatusConfig(
  status: IdentitySyncStatusValue,
): IdentityStatusConfig | undefined {
  return OUT_OF_SYNC_CONFIG[status as unknown as string]
}

// The repair action that resolves an out-of-sync identity: an email mismatch
// is fixed in place by editing the user; a legacy/missing login needs a full
// recreate. Returns undefined when there is nothing to fix.
export function getIdentityFix(
  status: IdentitySyncStatusValue,
): "change-email" | "recreate-login" | undefined {
  const value = status as unknown as string
  if (value === IDENTITY_SYNC_STATUS_EMAIL_MISMATCH) return "change-email"
  if (
    value === IDENTITY_SYNC_STATUS_NO_EMAIL_LOGIN ||
    value === IDENTITY_SYNC_STATUS_MISSING_IN_AZURE
  ) {
    return "recreate-login"
  }
  return undefined
}

// Whether the user signs in with Fusion login (Entra External ID, FUS-927).
// Gates the B2C-only repair actions (Recreate login) — the backend refuses
// them for these users regardless; hiding is the polish on top.
export function isFusionLoginIdentity(status: IdentitySyncStatusValue) {
  return status === IDENTITY_SYNC_STATUS_FUSION_LOGIN
}

// The "all good" / "not checked yet" presentation, used only where we always
// want to render a value (the detail panel), never in the table.
export function getIdentityHealthyDisplay(status: IdentitySyncStatusValue): {
  variant: DotProps["variant"]
  label: string
} {
  if ((status as unknown as string) === IDENTITY_SYNC_STATUS_IN_SYNC) {
    return { variant: "success-1", label: "In sync" }
  }
  return { variant: "muted", label: "Not yet checked" }
}
